const { logger, exceptionHandler } = require("../../utils")
const { isSSR, isWebView, jsonDataSantizer } = require("../../utils/platform")
const interfaceLibrary = require("../../interfaces")
const InterfaceConstructor = require("../../interfaces/template")

/**
 * WebCallbacks Handler
 * @param {string} interfaceName - name of the interface called
 * @param {string} methodName - name of the method within the interface called
 * @param {object|string|boolean} [args] - arguments passed inside the callback
 * @protected
 */
const callbackHandler = (interfaceName = "", methodName = "", args = {}) => {
    if (!isWebView())
        return logger.info(
            `WebCallback - "${interfaceName}.${methodName}" is being called in env other than webview`
        )

    const data = typeof args === "string" ? JSON.parse(jsonDataSantizer(args)) : args
    if (interfaceLibrary.hasSupport(interfaceName)) {
        const webInterface = interfaceLibrary.definitions[interfaceName]
        if (typeof webInterface.methods[methodName] === "function") {
            return webInterface.methods[methodName](data)
        } else {
            return logger.error(
                `WebCallback - "${interfaceName}.${methodName}" is not defined in the current scope`
            )
        }
    }
    return logger.error(`WebCallback - "${interfaceName}.${methodName}" is not defined in the current scope`)
}

/**
 * JS Proxy based method handler
 * @protected
 */
const methodHandler = {
    get: (target, prop) => {
        return exceptionHandler((args) => callbackHandler(target.interfaceName, prop, args))
    },
}

/**
 * JS Proxy based interface handler
 * @protected
 */
const interfaceHandler = {
    get: (_, prop) => {
        return new Proxy({ interfaceName: prop }, methodHandler)
    },
    set: (target, prop, value, receiver) => {
        return Reflect.set(target, prop, value, receiver)
    },
    ownKeys() {
        const webEvents = Object.keys(interfaceLibrary.definitions)
        return webEvents
    },
    getOwnPropertyDescriptor() {
        return {
            enumerable: true,
            configurable: true,
        }
    },
}

/**
 * Web Bridge Definition
 */
const webBridge = {
    /**
     * initializes web bridge
     * @param {object} config
     */
    initialize: (config = {}) => {
        if (isSSR()) return
        window.synapse[config.bridgeName] = new Proxy(
            window.synapse[config.bridgeName] || {},
            interfaceHandler
        )
    },
    /**
     * registers new web interfaces
     * @param {object} definitions - interfaces definition object
     * @param {boolean} [override] - overrides existing interface definitions
     */
    register: (definitions = {}, override = false) => {
        if (!isWebView())
            return logger.info("webBridge register method being called in env other than webview")
        const unregisteredInterfaces = Object.keys(definitions)
        unregisteredInterfaces.forEach((interfaceName) => {
            if (interfaceLibrary.hasSupport(interfaceName)) {
                const webInterface = interfaceLibrary.definitions[interfaceName]
                const interfaceMethods = definitions[interfaceName]
                Object.entries(interfaceMethods).forEach(([methodName, callback]) => {
                    if (webInterface.hasSupport(methodName) && !override)
                        return logger.info(
                            `interface - "${interfaceName}" with method - "${methodName}" already exists, ` +
                                `if you want to override the existing behaviour please set optional override key as true`
                        )
                    webInterface.set(methodName, callback)
                })
            } else {
                const newInterface = new InterfaceConstructor(interfaceName)
                const interfaceMethods = definitions[interfaceName]
                interfaceLibrary.set(newInterface)
                Object.entries(interfaceMethods).forEach(([methodName, callback]) => {
                    newInterface.set(methodName, callback)
                })
            }
        })
    },
}

module.exports = webBridge
